/**
 * Affichage d'un quiz à trous — design Carbon
 */

import { useState } from 'react'
import { Tile, Layer, Tag, Button } from '@carbon/react'
import { ArrowLeft, ArrowRight, Download, Renew } from '@carbon/icons-react'
import type { ClozeQuizSchema } from '@sphere/ai/domain'
import styles from './ClozeQuizDisplay.module.scss'

interface ClozeQuizDisplayProps {
  quiz: ClozeQuizSchema
  onDownload?: () => void
  onRestart?: () => void
  className?: string
}

interface ClozeQuizState {
  currentQuestion: number
  answers: Record<number, string[]>
  showResults: boolean
  score: number
}

export function ClozeQuizDisplay({
  quiz,
  onDownload,
  onRestart,
  className = '',
}: ClozeQuizDisplayProps) {
  const [quizState, setQuizState] = useState<ClozeQuizState>({
    currentQuestion: 0,
    answers: {},
    showResults: false,
    score: 0,
  })

  const currentQuestion = quiz.questions[quizState.currentQuestion]
  const isLastQuestion = quizState.currentQuestion === quiz.questions.length - 1

  if (!currentQuestion?.question) {
    return (
      <Layer className={`${styles.card} ${className}`}>
        <Tile className={`${styles.tile} ${styles.errorCard}`}>
          <p className={styles.errorText}>
            Erreur : question mal formatée. Veuillez régénérer le quiz.
          </p>
        </Tile>
      </Layer>
    )
  }

  const textParts = currentQuestion.question.split(/_{1,}/)
  const blanksCount = textParts.length - 1
  const currentAnswers =
    quizState.answers[quizState.currentQuestion] ||
    new Array(blanksCount).fill('')
  const hasAllAnswers = currentAnswers.every(a => a.trim().length > 0)

  const handleAnswerChange = (blankIndex: number, value: string) => {
    const newAnswers = [...currentAnswers]
    newAnswers[blankIndex] = value
    setQuizState(prev => ({
      ...prev,
      answers: { ...prev.answers, [prev.currentQuestion]: newAnswers },
    }))
  }

  const handleChoiceClick = (choice: string) => {
    const emptyIndex = currentAnswers.findIndex(a => !a.trim())
    const targetIndex = emptyIndex !== -1 ? emptyIndex : 0
    handleAnswerChange(targetIndex, choice)
  }

  const handleNext = () => {
    if (isLastQuestion) {
      let totalBlanks = 0
      let correctBlanks = 0
      quiz.questions.forEach((q, qi) => {
        const userAnswers = quizState.answers[qi] || []
        const blanks = q.question.split(/_{1,}/).length - 1
        totalBlanks += blanks
        for (let bi = 0; bi < blanks; bi++) {
          if (
            userAnswers[bi]?.trim().toLowerCase() === q.answer?.toLowerCase()
          ) {
            correctBlanks++
          }
        }
      })
      setQuizState(prev => ({
        ...prev,
        showResults: true,
        score: totalBlanks > 0 ? correctBlanks / totalBlanks : 0,
      }))
    } else {
      setQuizState(prev => ({
        ...prev,
        currentQuestion: prev.currentQuestion + 1,
      }))
    }
  }

  const handlePrevious = () => {
    setQuizState(prev => ({
      ...prev,
      currentQuestion: Math.max(0, prev.currentQuestion - 1),
    }))
  }

  const handleRestart = () => {
    setQuizState({
      currentQuestion: 0,
      answers: {},
      showResults: false,
      score: 0,
    })
  }

  const progress =
    ((quizState.currentQuestion + 1) / quiz.questions.length) * 100

  // Results view
  if (quizState.showResults) {
    const percentage = Math.round(quizState.score * 100)
    const scoreClass =
      percentage >= 80
        ? styles.scoreGood
        : percentage >= 60
          ? styles.scoreMedium
          : styles.scoreLow

    return (
      <Layer className={`${styles.card} ${className}`}>
        <Tile className={styles.tile}>
          <div className={styles.resultsHeader}>
            <h2 className={styles.resultsTitle}>Résultats du Quiz à Trous</h2>
            <div className={`${styles.resultsScore} ${scoreClass}`}>
              {percentage}%
            </div>
            <p className={styles.resultsSub}>Score de complétion</p>
          </div>

          <div className={styles.resultsList}>
            {quiz.questions.map((q, qi) => {
              const userAnswers = quizState.answers[qi] || []
              const parts = q.question.split(/_{1,}/)
              return (
                <div key={qi} className={styles.resultItem}>
                  <h4 className={styles.resultItemTitle}>Question {qi + 1}</h4>
                  <div className={styles.resultText}>
                    {parts.map((part, pi) => (
                      <span key={pi}>
                        {part}
                        {pi < parts.length - 1 && (
                          <span
                            className={
                              userAnswers[pi]?.trim().toLowerCase() ===
                              q.answer?.toLowerCase()
                                ? styles.blankCorrect
                                : styles.blankIncorrect
                            }
                          >
                            {userAnswers[pi] || '—'}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                  <p className={styles.expectedAnswer}>
                    Réponse attendue : {q.answer}
                  </p>
                </div>
              )
            })}
          </div>

          <div className={styles.resultsActions}>
            <Button kind="tertiary" renderIcon={Renew} onClick={handleRestart}>
              Recommencer
            </Button>
            {onDownload && (
              <Button
                kind="tertiary"
                renderIcon={Download}
                onClick={onDownload}
              >
                Télécharger
              </Button>
            )}
            {onRestart && (
              <Button kind="primary" onClick={onRestart}>
                Nouveau Quiz
              </Button>
            )}
          </div>
        </Tile>
      </Layer>
    )
  }

  // Question view
  return (
    <Layer className={`${styles.card} ${className}`}>
      <Tile className={styles.tile}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{quiz.title}</h1>
            <div className={styles.tags}>
              {quiz.metadata?.difficulty && (
                <Tag type="blue">{quiz.metadata.difficulty}</Tag>
              )}
              {quiz.metadata?.estimated_time && (
                <Tag type="outline">{quiz.metadata.estimated_time} min</Tag>
              )}
            </div>
          </div>
          {quiz.description && (
            <p className={styles.description}>{quiz.description}</p>
          )}

          <div className={styles.progressBar}>
            <div className={styles.progressInfo}>
              <span>
                Question {quizState.currentQuestion + 1} sur{' '}
                {quiz.questions.length}
              </span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className={styles.progressTrack}>
              <div
                className={styles.progressFill}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        <div className={styles.questionBlock}>
          <div className={styles.questionHeader}>
            <span className={styles.questionNumber}>
              {quizState.currentQuestion + 1}
            </span>
            <h3 className={styles.questionLabel}>
              Complétez les mots manquants
            </h3>
          </div>

          <div className={styles.textWithBlanks}>
            {textParts.map((part, pi) => (
              <span key={pi}>
                {part}
                {pi < textParts.length - 1 && (
                  <span
                    className={`${styles.blank} ${currentAnswers[pi] ? styles.blankFilled : ''}`}
                    onClick={() => handleAnswerChange(pi, '')}
                    title={currentAnswers[pi] ? 'Cliquer pour vider' : ''}
                  >
                    {currentAnswers[pi] || '…'}
                  </span>
                )}
              </span>
            ))}
          </div>

          <div className={styles.choicesSection}>
            <p className={styles.choicesLabel}>Choisissez un mot :</p>
            <div className={styles.choicesGrid}>
              {currentQuestion.choices?.map((choice, ci) => {
                const isUsed = currentAnswers.includes(choice)
                return (
                  <Button
                    key={ci}
                    kind="secondary"
                    size="sm"
                    disabled={isUsed}
                    onClick={() => handleChoiceClick(choice)}
                    className={styles.choiceBtn}
                  >
                    {choice}
                  </Button>
                )
              })}
            </div>
          </div>
        </div>

        <div className={styles.nav}>
          <Button
            kind="tertiary"
            renderIcon={ArrowLeft}
            onClick={handlePrevious}
            disabled={quizState.currentQuestion === 0}
          >
            Précédent
          </Button>
          <Button
            kind="primary"
            renderIcon={ArrowRight}
            onClick={handleNext}
            disabled={!hasAllAnswers}
          >
            {isLastQuestion ? 'Terminer' : 'Suivant'}
          </Button>
        </div>
      </Tile>
    </Layer>
  )
}
